/**
 * MinoriPage - Lista Minori
 * Il Diario di Bordo - SaaS
 * 
 * Miglioramenti UX v2 (29/12/2024):
 * - Tab Attivi/Archiviati con bordo colorato (verde/arancione)
 * - Banner contestuale quando in archivio
 * - Mini-stats in alto (Totale/Compilati/Da fare)
 * - "I Miei Minori" come lista compatta collassabile
 * - Pulsanti touch più grandi (min 44x44px)
 * - Clear button nella ricerca
 * - Pulsante "Compila" esplicito con stato
 * - Empty state con CTA
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { minoriApi, usersApi } from '../../services/api'
import { PageLoader, EmptyState, Card, ConfirmModal } from '../../components/ui'
import SlideUpPanel from '../../components/ui/SlideUpPanel'
import { 
  Baby, 
  Plus, 
  Search, 
  Archive, 
  MoreVertical, 
  Edit, 
  RotateCcw, 
  CheckCircle, 
  Clock, 
  FileText, 
  ChevronDown, 
  Phone, 
  MapPin, 
  Users,
  X,
  ArrowLeft
} from 'lucide-react'

export default function MinoriPage() {
  const [loading, setLoading] = useState(true)
  const [minori, setMinori] = useState([])
  const [archiviati, setArchiviati] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingMinore, setEditingMinore] = useState(null)
  const [confirmArchive, setConfirmArchive] = useState(null)
  const [mieiMinoriExpanded, setMieiMinoriExpanded] = useState(true)
  
  const { user, isCoordinatore } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()

  useEffect(() => { loadMinori() }, [])

  const loadMinori = async () => {
    try {
      setLoading(true)
      const [attiviRes, archiviatiRes] = await Promise.all([
        minoriApi.list({ attivo: true }),
        minoriApi.list({ attivo: false })
      ])
      const attiviData = attiviRes.data?.data?.minori || attiviRes.data?.data || attiviRes.data
      setMinori(Array.isArray(attiviData) ? attiviData : [])
      const archiviatiData = archiviatiRes.data?.data?.minori || archiviatiRes.data?.data || archiviatiRes.data
      setArchiviati(Array.isArray(archiviatiData) ? archiviatiData : [])
    } catch (error) {
      showError('Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const mieiMinori = minori.filter(m => m.educatoreReferente?._id === user?.id || m.educatoreReferente === user?.id)
  const filteredMinori = minori.filter(m => m.nomeCompleto.toLowerCase().includes(searchQuery.toLowerCase()))
  const filteredArchiviati = archiviati.filter(m => m.nomeCompleto.toLowerCase().includes(searchQuery.toLowerCase()))

  // Stats per mini-cards
  const statsCompilati = minori.filter(m => m.hasEntryToday).length
  const statsDaFare = minori.filter(m => !m.hasEntryToday).length

  const handleArchive = async (minore) => {
    try {
      await minoriApi.archive(minore._id)
      showSuccess(`${minore.nomeCompleto} archiviato`)
      setConfirmArchive(null)
      loadMinori()
    } catch (error) {
      showError('Errore')
    }
  }

  const handleRestore = async (minore) => {
    try {
      await minoriApi.restore(minore._id)
      showSuccess(`${minore.nomeCompleto} ripristinato`)
      loadMinori()
    } catch (error) {
      showError('Errore')
    }
  }

  const handleSave = async (data) => {
    try {
      if (editingMinore) {
        await minoriApi.update(editingMinore._id, data)
        showSuccess('Minore aggiornato')
      } else {
        await minoriApi.create(data)
        showSuccess('Minore creato')
      }
      setShowForm(false)
      setEditingMinore(null)
      loadMinori()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore')
    }
  }

  if (loading) return <PageLoader />

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Baby className="h-7 w-7 mr-2 text-primary-600" />
          Minori
        </h1>
        {isCoordinatore() && (
          <button 
            onClick={() => { setEditingMinore(null); setShowForm(true) }} 
            className="btn-primary flex items-center justify-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Minore
          </button>
        )}
      </div>

      {/* Mini Stats - solo vista attivi */}
      {!showArchived && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 text-center shadow-sm border border-gray-100 dark:border-gray-700">
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{minori.length}</p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Totale</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 sm:p-4 text-center shadow-sm border border-green-100 dark:border-green-800">
            <p className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">{statsCompilati}</p>
            <p className="text-xs sm:text-sm text-green-600 dark:text-green-400">✓ Compilati</p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-3 sm:p-4 text-center shadow-sm border border-yellow-100 dark:border-yellow-800">
            <p className="text-2xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400">{statsDaFare}</p>
            <p className="text-xs sm:text-sm text-yellow-600 dark:text-yellow-400">⏳ Da fare</p>
          </div>
        </div>
      )}

      {/* Ricerca + Tab */}
      <div className="space-y-3">
        {/* Ricerca con Clear button */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input 
            type="text" 
            placeholder="Cerca minore..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="input pl-10 pr-10 py-3 text-base" 
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        
        {/* Tab Attivi/Archiviati - full width su mobile */}
        <div className="grid grid-cols-2 gap-2">
          <button 
            onClick={() => setShowArchived(false)}
            className={`flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all ${
              !showArchived 
                ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-500 text-green-700 dark:text-green-400 shadow-sm' 
                : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <span className="mr-2">🟢</span>
            Attivi ({minori.length})
          </button>
          
          <button 
            onClick={() => setShowArchived(true)}
            className={`flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all ${
              showArchived 
                ? 'bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 text-amber-700 dark:text-amber-400 shadow-sm' 
                : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Archive className="h-4 w-4 mr-2" />
            Archiviati ({archiviati.length})
          </button>
        </div>
      </div>

      {/* Banner Archivio */}
      {showArchived && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center">
            <span className="text-2xl mr-3">📦</span>
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">Stai visualizzando i minori archiviati</p>
              <p className="text-sm text-amber-600 dark:text-amber-400">Questi minori non sono più attivi nel centro</p>
            </div>
          </div>
          <button 
            onClick={() => setShowArchived(false)}
            className="flex items-center justify-center text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Torna agli attivi
          </button>
        </div>
      )}

      {/* I Miei Minori - Lista Compatta Collassabile (solo vista attivi) */}
      {!showArchived && mieiMinori.length > 0 && (
        <MieiMinoriSection 
          minori={mieiMinori}
          expanded={mieiMinoriExpanded}
          onToggle={() => setMieiMinoriExpanded(!mieiMinoriExpanded)}
          onEdit={(m) => { setEditingMinore(m); setShowForm(true) }}
          onArchive={setConfirmArchive}
          isCoordinatore={isCoordinatore()}
          navigate={navigate}
        />
      )}

      {/* Lista principale */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {showArchived ? '📦 Minori Archiviati' : '📋 Tutti i Minori'}
        </h2>
        
        {(showArchived ? filteredArchiviati : filteredMinori).length > 0 ? (
          <div className={showArchived ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "divide-y divide-gray-100 dark:divide-gray-700"}>
            {showArchived ? (
              filteredArchiviati.map((minore) => (
                <MinoreArchivedCard 
                  key={minore._id} 
                  minore={minore} 
                  onRestore={() => handleRestore(minore)}
                  isCoordinatore={isCoordinatore()}
                />
              ))
            ) : (
              filteredMinori.map((minore) => (
                <MinoreListItem 
                  key={minore._id} 
                  minore={minore}
                  onEdit={() => { setEditingMinore(minore); setShowForm(true) }} 
                  onArchive={() => setConfirmArchive(minore)}
                  isCoordinatore={isCoordinatore()} 
                  navigate={navigate} 
                />
              ))
            )}
          </div>
        ) : (
          <EmptyStateWithCTA 
            isArchived={showArchived}
            isCoordinatore={isCoordinatore()}
            searchQuery={searchQuery}
            onClearSearch={() => setSearchQuery('')}
            onAddNew={() => { setEditingMinore(null); setShowForm(true) }}
          />
        )}
      </Card>

      {/* Slide Panel Form */}
      <SlideUpPanel 
        isOpen={showForm} 
        onClose={() => { setShowForm(false); setEditingMinore(null) }} 
        title={editingMinore ? 'Modifica Minore' : 'Nuovo Minore'}
      >
        <MinoreForm 
          minore={editingMinore} 
          onSave={handleSave} 
          onCancel={() => { setShowForm(false); setEditingMinore(null) }} 
        />
      </SlideUpPanel>

      {/* Confirm Modal Archiviazione */}
      <ConfirmModal 
        isOpen={!!confirmArchive} 
        onClose={() => setConfirmArchive(null)} 
        onConfirm={() => handleArchive(confirmArchive)} 
        title="Archivia Minore" 
        message={`Archiviare ${confirmArchive?.nomeCompleto}?`} 
        confirmText="Archivia" 
      />
    </div>
  )
}

// === NUOVI COMPONENTI ===

// Empty State con CTA
function EmptyStateWithCTA({ isArchived, isCoordinatore, searchQuery, onClearSearch, onAddNew }) {
  if (searchQuery) {
    return (
      <div className="text-center py-12">
        <div className="text-5xl mb-4">🔍</div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nessun risultato</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">Nessun minore trovato per "{searchQuery}"</p>
        <button 
          onClick={onClearSearch}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
        >
          Cancella ricerca
        </button>
      </div>
    )
  }
  
  if (isArchived) {
    return (
      <div className="text-center py-12">
        <div className="text-5xl mb-4">📦</div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nessun minore archiviato</h3>
        <p className="text-gray-500 dark:text-gray-400">I minori archiviati appariranno qui</p>
      </div>
    )
  }
  
  return (
    <div className="text-center py-12">
      <div className="text-5xl mb-4">🧒</div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nessun minore presente</h3>
      <p className="text-gray-500 dark:text-gray-400 mb-6">Inizia aggiungendo il primo minore al centro</p>
      {isCoordinatore && (
        <button 
          onClick={onAddNew}
          className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
        >
          <Plus className="h-5 w-5" />
          Aggiungi il primo minore
        </button>
      )}
    </div>
  )
}

// I Miei Minori - Sezione Collassabile
function MieiMinoriSection({ minori, expanded, onToggle, onEdit, onArchive, isCoordinatore, navigate }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button 
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-primary-50 dark:from-primary-900/20 to-white dark:to-gray-800 hover:from-primary-100 dark:hover:from-primary-900/30 transition-colors"
      >
        <span className="font-semibold text-gray-900 dark:text-white flex items-center">
          👥 I Miei Minori Assegnati 
          <span className="ml-2 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-sm px-2 py-0.5 rounded-full">
            {minori.length}
          </span>
        </span>
        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      
      {expanded && (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {minori.map(minore => (
            <div 
              key={minore._id}
              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <Link to={`/minori/${minore._id}`} className="flex items-center min-w-0 flex-1">
                <span className="text-lg mr-3">{minore.dataNascita && calcAge(minore.dataNascita) < 12 ? '🧒' : '👦'}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{minore.nomeCompleto}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {minore.dataNascita && `${calcAge(minore.dataNascita)} anni`}
                  </p>
                </div>
              </Link>
              
              {/* Pulsante Compila esplicito */}
              <button 
                onClick={() => navigate(`/entries/new/${minore._id}`)}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                  minore.hasEntryToday 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                }`}
              >
                {minore.hasEntryToday ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span className="hidden sm:inline">Fatto</span>
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    <span className="hidden sm:inline">Compila</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Card Minore Archiviato
function MinoreArchivedCard({ minore, onRestore, isCoordinatore }) {
  return (
    <div className="relative p-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 opacity-80 hover:opacity-100 transition-all">
      <div className="flex items-start justify-between">
        <Link to={`/minori/${minore._id}`} className="flex-1 min-w-0">
          <p className="font-semibold text-gray-700 dark:text-gray-300">{minore.nomeCompleto}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {minore.dataNascita && `${calcAge(minore.dataNascita)} anni`}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            📦 Archiviato il {minore.archiviatoAt ? new Date(minore.archiviatoAt).toLocaleDateString('it-IT') : '-'}
          </p>
        </Link>
        {isCoordinatore && (
          <button 
            onClick={onRestore}
            className="flex items-center text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 px-3 py-2 rounded-lg transition-colors min-h-[44px]"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Ripristina
          </button>
        )}
      </div>
    </div>
  )
}

// Lista Item con pulsanti touch-friendly
function MinoreListItem({ minore, onEdit, onArchive, isCoordinatore, navigate }) {
  return (
    <div className="flex items-center justify-between py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 -mx-4 lg:-mx-6 px-4 lg:px-6">
      <Link to={`/minori/${minore._id}`} className="flex items-center flex-1 min-w-0">
        <span className="text-xl mr-3">{minore.dataNascita && calcAge(minore.dataNascita) < 12 ? '🧒' : '👦'}</span>
        <div className="min-w-0">
          <p className="font-medium text-gray-900 dark:text-white truncate">{minore.nomeCompleto}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            Ref: {minore.educatoreReferente?.nome || 'Non assegnato'}
            {minore.dataNascita && ` • ${calcAge(minore.dataNascita)} anni`}
          </p>
        </div>
      </Link>
      
      {/* Pulsanti touch-friendly (min 44x44px) */}
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Pulsante compila */}
        <button 
          onClick={() => navigate(`/entries/new/${minore._id}`)}
          className={`flex items-center justify-center min-w-[44px] min-h-[44px] p-2 rounded-lg transition-colors ${
            minore.hasEntryToday 
              ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30' 
              : 'text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30'
          }`}
          title={minore.hasEntryToday ? 'Già compilato' : 'Compila diario'}
        >
          {minore.hasEntryToday ? <CheckCircle className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
        </button>
        
        {isCoordinatore && (
          <>
            {/* Pulsante modifica */}
            <button 
              onClick={onEdit}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Modifica"
            >
              <Edit className="h-5 w-5" />
            </button>
            
            {/* Pulsante archivia */}
            <button 
              onClick={onArchive}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-orange-500 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg transition-colors"
              title="Archivia"
            >
              <Archive className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// === FORM MINORE (invariato) ===

function MinoreForm({ minore, onSave, onCancel }) {
  const [showContatti, setShowContatti] = useState(false)
  const [loading, setLoading] = useState(false)
  const [educatori, setEducatori] = useState([])

  // Estrai contatti esistenti dal minore
  const getPadre = () => minore?.contattiFamiglia?.find(c => c.relazione === 'padre') || {}
  const getMadre = () => minore?.contattiFamiglia?.find(c => c.relazione === 'madre') || {}
  const getAltro = () => minore?.contattiFamiglia?.find(c => !['padre', 'madre'].includes(c.relazione)) || {}

  const [formData, setFormData] = useState({
    nomeCompleto: minore?.nomeCompleto || '',
    dataNascita: minore?.dataNascita?.split('T')[0] || '',
    codiceFiscale: minore?.codiceFiscale || '',
    telefono: minore?.telefono || '',
    educatoreReferente: minore?.educatoreReferente?._id || minore?.educatoreReferente || '',
    via: minore?.residenza?.via || '',
    citta: minore?.residenza?.citta || '',
    cap: minore?.residenza?.cap || '',
    padreNome: getPadre().nome || '',
    padreTelefono: getPadre().telefono || '',
    madreNome: getMadre().nome || '',
    madreTelefono: getMadre().telefono || '',
    altroEtichetta: getAltro().relazione || 'Altro',
    altroNome: getAltro().nome || '',
    altroTelefono: getAltro().telefono || '',
    note: minore?.note || ''
  })

  useEffect(() => {
    const loadEducatori = async () => {
      try {
        const res = await usersApi.list({ attivo: true })
        const users = res.data?.data?.users || res.data?.data || res.data || []
        setEducatori(Array.isArray(users) ? users.filter(u => u.attivo !== false) : [])
      } catch (error) {
        console.error('Errore caricamento educatori:', error)
      }
    }
    loadEducatori()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    // Costruisci oggetto per API
    const contattiFamiglia = []
    if (formData.padreNome || formData.padreTelefono) {
      contattiFamiglia.push({ relazione: 'padre', nome: formData.padreNome, telefono: formData.padreTelefono })
    }
    if (formData.madreNome || formData.madreTelefono) {
      contattiFamiglia.push({ relazione: 'madre', nome: formData.madreNome, telefono: formData.madreTelefono })
    }
    if (formData.altroNome || formData.altroTelefono) {
      contattiFamiglia.push({ relazione: formData.altroEtichetta || 'Altro', nome: formData.altroNome, telefono: formData.altroTelefono })
    }
    
    const payload = {
      nomeCompleto: formData.nomeCompleto,
      dataNascita: formData.dataNascita || undefined,
      codiceFiscale: formData.codiceFiscale || undefined,
      telefono: formData.telefono || undefined,
      educatoreReferente: formData.educatoreReferente || undefined,
      residenza: (formData.via || formData.citta || formData.cap) ? {
        via: formData.via,
        citta: formData.citta,
        cap: formData.cap
      } : undefined,
      contattiFamiglia: contattiFamiglia.length > 0 ? contattiFamiglia : undefined,
      note: formData.note || undefined
    }
    
    try {
      await onSave(payload)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Nome e Data nascita */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nome Completo <span className="text-red-500">*</span>
          </label>
          <input 
            type="text" 
            value={formData.nomeCompleto} 
            onChange={(e) => setFormData({ ...formData, nomeCompleto: e.target.value })} 
            className="input" 
            required 
            placeholder="Nome e Cognome"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data di Nascita</label>
          <input 
            type="date" 
            value={formData.dataNascita} 
            onChange={(e) => setFormData({ ...formData, dataNascita: e.target.value })} 
            className="input" 
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Codice Fiscale</label>
          <input 
            type="text" 
            value={formData.codiceFiscale} 
            onChange={(e) => setFormData({ ...formData, codiceFiscale: e.target.value.toUpperCase() })} 
            className="input" 
            maxLength={16} 
            placeholder="RSSMRA00A01H501Z"
          />
        </div>
      </div>

      {/* Telefono */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          <Phone className="h-4 w-4 inline mr-1" />Telefono
        </label>
        <input 
          type="tel" 
          value={formData.telefono} 
          onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} 
          className="input" 
          placeholder="+39 333 1234567"
        />
      </div>

      {/* Residenza */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          <MapPin className="h-4 w-4 inline mr-1" />Residenza
        </label>
        <input 
          type="text" 
          value={formData.via} 
          onChange={(e) => setFormData({ ...formData, via: e.target.value })} 
          className="input mb-2" 
          placeholder="Via/Indirizzo"
        />
        <div className="grid grid-cols-3 gap-2">
          <input 
            type="text" 
            value={formData.citta} 
            onChange={(e) => setFormData({ ...formData, citta: e.target.value })} 
            className="input col-span-2" 
            placeholder="Città"
          />
          <input 
            type="text" 
            value={formData.cap} 
            onChange={(e) => setFormData({ ...formData, cap: e.target.value })} 
            className="input" 
            placeholder="CAP"
            maxLength={5}
          />
        </div>
      </div>

      {/* Sezione Contatti Familiari - Collassabile */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowContatti(!showContatti)}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
        >
          <span className="flex items-center font-medium text-gray-700 dark:text-gray-300">
            <Users className="h-4 w-4 mr-2" />Contatti Familiari
          </span>
          <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${showContatti ? 'rotate-180' : ''}`} />
        </button>
        
        {showContatti && (
          <div className="p-4 space-y-4 bg-white dark:bg-gray-800">
            {/* Padre */}
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">👨 Padre</label>
              <div className="grid grid-cols-2 gap-2">
                <input 
                  type="text" 
                  value={formData.padreNome} 
                  onChange={(e) => setFormData({ ...formData, padreNome: e.target.value })} 
                  className="input" 
                  placeholder="Nome completo"
                />
                <input 
                  type="tel" 
                  value={formData.padreTelefono} 
                  onChange={(e) => setFormData({ ...formData, padreTelefono: e.target.value })} 
                  className="input" 
                  placeholder="Telefono"
                />
              </div>
            </div>

            {/* Madre */}
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">👩 Madre</label>
              <div className="grid grid-cols-2 gap-2">
                <input 
                  type="text" 
                  value={formData.madreNome} 
                  onChange={(e) => setFormData({ ...formData, madreNome: e.target.value })} 
                  className="input" 
                  placeholder="Nome completo"
                />
                <input 
                  type="tel" 
                  value={formData.madreTelefono} 
                  onChange={(e) => setFormData({ ...formData, madreTelefono: e.target.value })} 
                  className="input" 
                  placeholder="Telefono"
                />
              </div>
            </div>

            {/* Altro (con etichetta modificabile) */}
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                📋 
                <input 
                  type="text" 
                  value={formData.altroEtichetta} 
                  onChange={(e) => setFormData({ ...formData, altroEtichetta: e.target.value })} 
                  className="ml-1 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:border-primary-500 outline-none w-32 text-sm"
                  placeholder="Altro"
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input 
                  type="text" 
                  value={formData.altroNome} 
                  onChange={(e) => setFormData({ ...formData, altroNome: e.target.value })} 
                  className="input" 
                  placeholder="Nome completo"
                />
                <input 
                  type="tel" 
                  value={formData.altroTelefono} 
                  onChange={(e) => setFormData({ ...formData, altroTelefono: e.target.value })} 
                  className="input" 
                  placeholder="Telefono"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Educatore Referente */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          <Users className="h-4 w-4 inline mr-1" />Educatore Referente
        </label>
        <select
          value={formData.educatoreReferente}
          onChange={(e) => setFormData({ ...formData, educatoreReferente: e.target.value })}
          className="input"
        >
          <option value="">-- Nessun referente --</option>
          {educatori.map((edu) => (
            <option key={edu._id} value={edu._id}>
              {edu.nome} {edu.cognome} {edu.role === 'coordinatore' ? '(Coord.)' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Note */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note</label>
        <textarea 
          value={formData.note} 
          onChange={(e) => setFormData({ ...formData, note: e.target.value })} 
          className="input" 
          rows={3} 
          placeholder="Note aggiuntive..."
        />
      </div>

      {/* Bottoni */}
      <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
        <button type="button" onClick={onCancel} className="btn-secondary">Annulla</button>
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Salvataggio...' : minore ? 'Aggiorna' : 'Crea'}
        </button>
      </div>
    </form>
  )
}

// Helper per calcolo età
function calcAge(d) { 
  const t = new Date()
  const b = new Date(d)
  let a = t.getFullYear() - b.getFullYear()
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--
  return a 
}
